import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { EyebrowLabel } from "../../../../../components/UIToolkit/molecules/EyebrowLabel";
import { CONFIG_TABLE_CONTAINER_SX, CONFIG_TABLE_HEADER_CELL_SX } from "../../../../../components/UIToolkit/Tables/ConfigTable/configTableSx";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";
import { JobsEditRow } from "./JobsEditRow";
import type { JobsEditState } from "./useJobsEditState";

type Props = {
	jobsEdit: JobsEditState;
	employeeName: string;
	onSetPrimaryJob: (jobId: string) => void;
};

const noteSx = { fontSize: FONT_SIZE.CAPTION, lineHeight: 1.45, px: 0.5 } as const;

/** With one job there is nothing to pick between, so the table stays hidden. */
const MIN_JOBS_FOR_TABLE = 2;

/**
 * Edit-mode Job table, shown once there are two or more jobs. The dropdown above only picks jobs.
 * It spans the whole field grid so the four columns have room.
 */
export const JobsEditTable: FC<Props> = ({ jobsEdit, employeeName, onSetPrimaryJob }) => {
	const { t } = useTranslation();
	const { rows, isLoadingNotifications, isNotificationsError, setRowSilent, requestRemove } = jobsEdit;
	if (rows.length < MIN_JOBS_FOR_TABLE) return null;
	const notifyingCount = rows.filter((row) => row.isSilent === false).length;
	// Also on a new profile, so the admin learns why the jobs that aren't the primary start off.
	const hasNewSilent = rows.some((row) => row.isAdded && row.isSilent === true);

	return (
		<Box data-testid="profile-job-settings" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
				<EyebrowLabel component="h3">{t("jobNotifications.profile.table.title")}</EyebrowLabel>
				<Typography data-testid="profile-job-settings-summary" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
					{`· ${t("jobNotifications.profile.table.eligibleCount", { count: rows.length })} · ${t("jobNotifications.profile.notifyingCount", { count: notifyingCount })}`}
				</Typography>
			</Box>
			<Box sx={CONFIG_TABLE_CONTAINER_SX}>
				<Table size="small" aria-label={t("jobNotifications.profile.table.title")}>
					<TableHead>
						<TableRow>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX}>{t("job")}</TableCell>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX} align="center">
								{t("primary")}
							</TableCell>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX}>{t("notifications")}</TableCell>
							<TableCell sx={CONFIG_TABLE_HEADER_CELL_SX}>
								<Box component="span" sx={visuallyHidden}>
									{t("jobNotifications.profile.table.remove")}
								</Box>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map((row) => (
							<JobsEditRow
								key={row.id}
								row={row}
								isLoading={isLoadingNotifications}
								isError={isNotificationsError}
								onSetPrimaryJob={onSetPrimaryJob}
								onSetSilent={(isSilent) => setRowSilent(row.id, isSilent, "edit_table")}
								onRemove={() => requestRemove(row.id)}
							/>
						))}
					</TableBody>
				</Table>
			</Box>
			{hasNewSilent && (
				<Typography role="status" data-testid="profile-job-settings-new-muted-note" sx={{ ...noteSx, color: "warning.dark" }}>
					{t("jobNotifications.profile.table.newMutedNote", { name: employeeName })}
				</Typography>
			)}
			{isNotificationsError && (
				<Typography role="alert" sx={{ ...noteSx, color: "error.dark" }}>
					{t("jobNotifications.profile.table.loadFailed")}
				</Typography>
			)}
			<Typography sx={{ ...noteSx, color: INK.MUTED }}>{t("jobNotifications.profile.mutingKeepsEligible")}</Typography>
		</Box>
	);
};
